import { useParams, Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Chip,
} from "@heroui/react";
import {
    LuArrowLeft,
    LuArrowDownRight,
    LuArrowUpRight,
    LuPackage,
} from "react-icons/lu";
import { useDataTable } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    FilterButton,
    ActiveFilterChips,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import { formatThousands } from "@/utils";
import { FilterHistory } from "./components";

// Initial filter values
const initialFilters = {
    date_from: "",
    date_to: "",
    activity_source: "",
};

// Skeleton columns
const skeletonColumns = [
    { name: "No.", skeletonWidth: "w-8" },
    { name: "Tanggal", skeletonWidth: "w-24" },
    { name: "Masuk", skeletonWidth: "w-20", align: "end" },
    { name: "Keluar", skeletonWidth: "w-20", align: "end" },
    { name: "Saldo", skeletonWidth: "w-20", align: "end" },
    { name: "Sumber", skeletonWidth: "w-24" },
    { name: "Keterangan", skeletonWidth: "w-32" },
];

// Format date
const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(new Date(dateString));
};

// Activity source config
const activityConfig = {
    initial_stock: { label: "Stok Awal", color: "primary" },
    purchase: { label: "Pembelian", color: "success" },
    sales: { label: "Penjualan", color: "warning" },
    adjustment: { label: "Penyesuaian", color: "secondary" },
    transfer: { label: "Transfer", color: "default" },
    production: { label: "Produksi", color: "primary" },
    return: { label: "Retur", color: "danger" },
};

const StockHistory = () => {
    const { warehouseId, materialId, unitId } = useParams();

    const table = useDataTable(
        `stocks/history/warehouse/${warehouseId}/material/${materialId}/unit/${unitId}`,
        null,
        {},
        initialFilters,
    );
    const { filterPanel } = table;

    // Get stock info from first item
    const stockInfo = table.items?.[0];
    const materialName = stockInfo?.material?.name || "Produk";
    const warehouseName = stockInfo?.warehouse?.name || "Gudang";
    const unitName = stockInfo?.unit?.name || "-";

    // Filter labels with getValueLabel
    const filterLabels = {
        date_from: { label: "Dari Tanggal" },
        date_to: { label: "Sampai Tanggal" },
        activity_source: {
            label: "Sumber",
            getValueLabel: (val) => activityConfig[val]?.label || val,
        },
    };

    // Handle remove filter
    const handleRemoveFilter = (key) => {
        if (key === "date_from" || key === "date_to") {
            filterPanel.setPending((prev) => ({
                ...prev,
                date_from: "",
                date_to: "",
            }));
            table.filters.setFilter("date_from", "");
            table.filters.setFilter("date_to", "");
        } else {
            filterPanel.setPending((prev) => ({
                ...prev,
                [key]: initialFilters[key],
            }));
            table.filters.setFilter(key, initialFilters[key]);
        }
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Riwayat Stok"
                    description="Terjadi kesalahan saat memuat data."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div className="flex gap-4">
                    <Button
                        as={Link}
                        to="/stocks"
                        isIconOnly
                        variant="light"
                        size="sm"
                    >
                        <LuArrowLeft size={18} />
                    </Button>
                    <PageHeader
                        title="Riwayat Stok"
                        description={`${materialName} - ${warehouseName} (${unitName})`}
                        breadcrumbs={[
                            { label: "Dashboard", href: "/" },
                            { label: "Stok", href: "/stocks" },
                            { label: "Riwayat" },
                        ]}
                    />
                </div>
                <FilterButton
                    isOpen={filterPanel.isOpen}
                    onToggle={() => filterPanel.setIsOpen(!filterPanel.isOpen)}
                    activeCount={table.filters.activeCount}
                />
            </div>

            {/* Filter Panel */}
            <FilterHistory table={table} initialFilters={initialFilters} />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            icon={LuPackage}
                            title="Belum Ada Riwayat"
                            description="Riwayat stok untuk item ini belum tersedia."
                        />
                    ) : (
                        <Table aria-label="Tabel Riwayat Stok" shadow="none">
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="date">Tanggal</TableColumn>
                                <TableColumn key="in" align="end">
                                    Masuk
                                </TableColumn>
                                <TableColumn key="out" align="end">
                                    Keluar
                                </TableColumn>
                                <TableColumn key="balance" align="end">
                                    Saldo
                                </TableColumn>
                                <TableColumn key="source">Sumber</TableColumn>
                                <TableColumn key="desc">Keterangan</TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => {
                                    const source = activityConfig[
                                        item.activity_source
                                    ] || {
                                        label: item.activity_source || "-",
                                        color: "default",
                                    };
                                    const isIn = (item.qty_in || 0) > 0;
                                    const isOut = (item.qty_out || 0) > 0;

                                    return (
                                        <TableRow key={item.id}>
                                            <TableCell>
                                                <ThemedText
                                                    size="sm"
                                                    variant="secondary"
                                                >
                                                    {(table.pagination.page -
                                                        1) *
                                                        table.pagination
                                                            .perPage +
                                                        table.items.indexOf(
                                                            item,
                                                        ) +
                                                        1}
                                                    .
                                                </ThemedText>
                                            </TableCell>
                                            <TableCell>
                                                <ThemedText size="sm">
                                                    {formatDate(item.date)}
                                                </ThemedText>
                                            </TableCell>
                                            <TableCell>
                                                {isIn ? (
                                                    <div className="text-success flex items-center justify-end gap-1">
                                                        <LuArrowDownRight
                                                            size={14}
                                                        />
                                                        <ThemedText size="sm">
                                                            +
                                                            {formatThousands(
                                                                item.qty_in,
                                                            )}
                                                        </ThemedText>
                                                    </div>
                                                ) : (
                                                    <ThemedText
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        -
                                                    </ThemedText>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {isOut ? (
                                                    <div className="text-danger flex items-center justify-end gap-1">
                                                        <LuArrowUpRight
                                                            size={14}
                                                        />
                                                        <ThemedText size="sm">
                                                            -
                                                            {formatThousands(
                                                                item.qty_out,
                                                            )}
                                                        </ThemedText>
                                                    </div>
                                                ) : (
                                                    <ThemedText
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        -
                                                    </ThemedText>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <ThemedText
                                                    size="sm"
                                                    className="font-medium"
                                                >
                                                    {formatThousands(
                                                        item.running_balance,
                                                    )}
                                                </ThemedText>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    color={source.color}
                                                    size="sm"
                                                    variant="flat"
                                                >
                                                    {source.label}
                                                </Chip>
                                            </TableCell>
                                            <TableCell>
                                                <ThemedText
                                                    size="xs"
                                                    variant="caption"
                                                    className="line-clamp-2"
                                                >
                                                    {item.description ||
                                                        item.storage_location ||
                                                        "-"}
                                                </ThemedText>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default StockHistory;
